/** Operations on flat sparse tile arrays [x, y, p, f, ...]. */

export const REMOVE = -2;

export function cellIndex(tiles: number[], x: number, y: number): number {
  for (let i = 0; i < tiles.length; i += 4) if (tiles[i] === x && tiles[i + 1] === y) return i;
  return -1;
}

export function getCell(tiles: number[], x: number, y: number): [number, number] | null {
  const i = cellIndex(tiles, x, y);
  return i < 0 ? null : [tiles[i + 2], tiles[i + 3]];
}

/** Returns a new array with the given cells set. p = REMOVE deletes the entry; p = -1 is an erase marker. */
export function setCells(tiles: number[], cells: { x: number; y: number; p: number; f: number }[]): number[] {
  if (!cells.length) return tiles;
  const map = new Map<string, { x: number; y: number; p: number; f: number }>();
  for (const c of cells) map.set(`${c.x},${c.y}`, c);
  const out: number[] = [];
  for (let i = 0; i < tiles.length; i += 4) {
    const k = `${tiles[i]},${tiles[i + 1]}`;
    const c = map.get(k);
    if (c) {
      map.delete(k);
      if (c.p !== REMOVE) out.push(c.x, c.y, c.p, c.f);
    } else {
      out.push(tiles[i], tiles[i + 1], tiles[i + 2], tiles[i + 3]);
    }
  }
  for (const c of map.values()) if (c.p !== REMOVE) out.push(c.x, c.y, c.p, c.f);
  return out;
}

export function cellsInRect(tiles: number[], x0: number, y0: number, x1: number, y1: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < tiles.length; i += 4) {
    const x = tiles[i], y = tiles[i + 1];
    if (x >= x0 && x <= x1 && y >= y0 && y <= y1) out.push(x, y, tiles[i + 2], tiles[i + 3]);
  }
  return out;
}

export function tileBounds(tiles: number[]): [number, number, number, number] | null {
  if (!tiles.length) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (let i = 0; i < tiles.length; i += 4) {
    if (tiles[i + 2] < 0) continue;
    x0 = Math.min(x0, tiles[i]); y0 = Math.min(y0, tiles[i + 1]);
    x1 = Math.max(x1, tiles[i]); y1 = Math.max(y1, tiles[i + 1]);
  }
  return Number.isFinite(x0) ? [x0, y0, x1 + 1, y1 + 1] : null;
}

/** Cells covered by a square brush of `size` centred on (x, y). */
export function brushCells(x: number, y: number, size: number): [number, number][] {
  const out: [number, number][] = [];
  const r0 = -Math.floor((size - 1) / 2);
  for (let dx = 0; dx < size; dx++) for (let dy = 0; dy < size; dy++) out.push([x + r0 + dx, y + r0 + dy]);
  return out;
}

/** Cells on a line between two cells (Bresenham), for continuous brush strokes. */
export function lineCells(x0: number, y0: number, x1: number, y1: number): [number, number][] {
  const out: [number, number][] = [];
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (let i = 0; i < 10000; i++) {
    out.push([x0, y0]);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
  return out;
}
